/**
 * Error Logs Viewer
 * Section 6.5 - Error monitoring dashboard
 */

import { useState } from 'react';

function ErrorLogs() {
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedError, setSelectedError] = useState(null);

  // Mock data for demo (will be replaced with API call)
  const mockLogs = [
    {
      id: 1,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      endpoint: '/api/pricing',
      errorCode: 'VEHICLE_NOT_SUPPORTED',
      message: 'Electric vehicle not supported for oil change',
      requestData: { year: 2023, make: 'Tesla', model: 'Model 3' },
      resolved: false
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      endpoint: '/api/bookings',
      errorCode: 'SLOT_UNAVAILABLE',
      message: 'Time slot already booked',
      requestData: { date: 'December 15, 2025', time: '10:00 am' },
      resolved: true
    },
    {
      id: 3,
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      endpoint: '/api/vehicles',
      errorCode: 'ENGINE_DATA_INCOMPLETE',
      message: 'Missing oil capacity for vehicle',
      requestData: { year: 2019, make: 'Rivian', model: 'R1T' },
      resolved: false
    }
  ];

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getTimeDiff = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) {
      const mins = Math.floor(diff / (1000 * 60));
      return `${mins} min ago`;
    }
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const errorCodeColors = {
    'VEHICLE_NOT_SUPPORTED': '#f59e0b',
    'SLOT_UNAVAILABLE': '#6366f1',
    'ENGINE_DATA_INCOMPLETE': '#ef4444',
    'SERVER_ERROR': '#dc2626',
    'NETWORK_ERROR': '#6b7280'
  };

  // Group by error code for summary
  const errorSummary = mockLogs.reduce((acc, log) => {
    acc[log.errorCode] = (acc[log.errorCode] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="error-logs">
      <div className="admin-page-header">
        <h1>Error Logs</h1>
        <p>Monitor API errors and issues</p>
      </div>

      {/* Time Range Filter */}
      <div className="admin-card">
        <div className="filter-row">
          <label>Time Range:</label>
          <div className="time-buttons">
            {['24h', '7d', '30d'].map(range => (
              <button
                key={range}
                className={`admin-btn ${timeRange === range ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                onClick={() => setTimeRange(range)}
              >
                Last {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error Summary */}
      <div className="admin-card">
        <h2>Error Summary</h2>
        <div className="summary-grid">
          {Object.entries(errorSummary).map(([code, count]) => (
            <div
              key={code}
              className="summary-item"
              style={{ borderLeftColor: errorCodeColors[code] || '#6b7280' }}
            >
              <div className="summary-count">{count}</div>
              <div className="summary-code">{code}</div>
            </div>
          ))}
          {Object.keys(errorSummary).length === 0 && (
            <p className="no-errors">No errors in this time range</p>
          )}
        </div>
      </div>

      {/* Error List */}
      <div className="admin-card">
        <h2>Recent Errors</h2>
        <div className="error-list">
          {mockLogs.map(log => (
            <div
              key={log.id}
              className={`error-item ${log.resolved ? 'resolved' : ''}`}
              onClick={() => setSelectedError(log)}
            >
              <div className="error-main">
                <div className="error-header">
                  <span
                    className="error-badge"
                    style={{ backgroundColor: errorCodeColors[log.errorCode] || '#6b7280' }}
                  >
                    {log.errorCode}
                  </span>
                  <span className="error-time">{getTimeDiff(log.timestamp)}</span>
                  {log.resolved && <span className="resolved-badge">Resolved</span>}
                </div>
                <div className="error-message">{log.message}</div>
                <div className="error-endpoint">{log.endpoint}</div>
              </div>
              <div className="error-arrow">→</div>
            </div>
          ))}
        </div>
      </div>

      {/* Error Detail Modal */}
      {selectedError && (
        <div className="modal-overlay" onClick={() => setSelectedError(null)}>
          <div className="detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Error Details</h2>
              <button className="modal-close" onClick={() => setSelectedError(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Error Code:</label>
                <span
                  className="error-badge"
                  style={{ backgroundColor: errorCodeColors[selectedError.errorCode] }}
                >
                  {selectedError.errorCode}
                </span>
              </div>
              <div className="detail-row">
                <label>Timestamp:</label>
                <span>{formatTimestamp(selectedError.timestamp)}</span>
              </div>
              <div className="detail-row">
                <label>Endpoint:</label>
                <code>{selectedError.endpoint}</code>
              </div>
              <div className="detail-row">
                <label>Message:</label>
                <span>{selectedError.message}</span>
              </div>
              <div className="detail-row">
                <label>Request Data:</label>
                <pre>{JSON.stringify(selectedError.requestData, null, 2)}</pre>
              </div>
              <div className="detail-row">
                <label>Status:</label>
                <span className={selectedError.resolved ? 'status-resolved' : 'status-open'}>
                  {selectedError.resolved ? 'Resolved' : 'Open'}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              {!selectedError.resolved && (
                <button
                  className="admin-btn admin-btn-primary"
                  onClick={() => alert('Mark as resolved functionality requires API')}
                >
                  Mark as Resolved
                </button>
              )}
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setSelectedError(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .filter-row {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .filter-row label {
          font-weight: 500;
          color: #374151;
        }

        .time-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .summary-item {
          padding: 1rem;
          background: #f9fafb;
          border-radius: 8px;
          border-left: 4px solid;
        }

        .summary-count {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
        }

        .summary-code {
          font-size: 0.75rem;
          color: #6b7280;
          font-family: monospace;
        }

        .no-errors {
          color: #6b7280;
          grid-column: span 3;
          text-align: center;
          padding: 2rem;
        }

        .error-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .error-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .error-item:hover {
          transform: translateX(4px);
        }

        .error-item.resolved {
          background: #f9fafb;
          border-color: #e5e7eb;
        }

        .error-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .error-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
          color: white;
          font-family: monospace;
        }

        .error-time {
          font-size: 0.8rem;
          color: #6b7280;
        }

        .resolved-badge {
          font-size: 0.75rem;
          padding: 0.125rem 0.5rem;
          background: #d1fae5;
          color: #059669;
          border-radius: 4px;
        }

        .error-message {
          font-weight: 500;
          color: #111827;
        }

        .error-endpoint {
          font-size: 0.8rem;
          color: #6b7280;
          font-family: monospace;
        }

        .error-arrow {
          color: #9ca3af;
          font-size: 1.25rem;
        }

        /* Modal styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .detail-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }

        .modal-body {
          padding: 1.5rem;
          overflow-y: auto;
        }

        .detail-row {
          margin-bottom: 1rem;
        }

        .detail-row label {
          display: block;
          font-size: 0.75rem;
          text-transform: uppercase;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }

        .detail-row code {
          background: #f3f4f6;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }

        .detail-row pre {
          background: #f3f4f6;
          padding: 1rem;
          border-radius: 8px;
          overflow-x: auto;
          font-size: 0.875rem;
          margin: 0;
        }

        .status-resolved {
          color: #059669;
          font-weight: 500;
        }

        .status-open {
          color: #dc2626;
          font-weight: 500;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }
      `}</style>
    </div>
  );
}

export default ErrorLogs;
